import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { AdsTab } from "../components/AdsTab";
import { FindLeadsSheet } from "../components/FindLeadsSheet";
import { Tabs } from "../components/Tabs";
import { Cleaners } from "./Cleaners";
import { Contracts } from "./Contracts";
import { CleanerLeads, ClientLeads } from "./Leads";

/**
 * The admin has two sections, because the business has two sides: people who do
 * the work, and the work itself. Everything else is a tab within one of them.
 *
 * The alternative -- a flat list of Leads, Clients, Contracts, Cleaners and a
 * search screen -- made you hold the relationships in your head. This way the
 * pipeline reads top to bottom: find leads, work them, and they become the thing
 * in the first tab.
 */

interface Summary {
  leads: { demandNew: number; supplyNew: number };
}

function useNewLeadCount(side: "demand" | "supply"): number | undefined {
  const { data } = useQuery({ queryKey: ["summary"], queryFn: api.summary });
  const summary = data as Summary | undefined;
  return side === "demand" ? summary?.leads.demandNew : summary?.leads.supplyNew;
}

export function CleanersSection({ tab }: { tab: string }) {
  const newLeads = useNewLeadCount("supply");
  const [finding, setFinding] = useState(false);

  return (
    <>
      <Tabs
        base="/cleaners"
        current={tab}
        tabs={[
          { slug: "", label: "Cleaners" },
          { slug: "leads", label: "Leads", count: newLeads },
          { slug: "ads", label: "Ads" },
        ]}
      />

      {tab === "" && <Cleaners />}
      {tab === "leads" && (
        <>
          <FindMoreButton onClick={() => setFinding(true)} />
          <CleanerLeads />
        </>
      )}
      {tab === "ads" && <AdsTab purpose="recruiting" />}

      {finding && <FindLeadsSheet side="supply" onClose={() => setFinding(false)} />}
    </>
  );
}

export function ContractsSection({ tab }: { tab: string }) {
  const newLeads = useNewLeadCount("demand");
  const [finding, setFinding] = useState(false);

  return (
    <>
      <Tabs
        base="/contracts"
        current={tab}
        tabs={[
          { slug: "", label: "Contracts" },
          { slug: "leads", label: "Leads", count: newLeads },
          { slug: "ads", label: "Ads" },
        ]}
      />

      {tab === "" && <Contracts />}
      {tab === "leads" && (
        <>
          <FindMoreButton onClick={() => setFinding(true)} />
          <ClientLeads />
        </>
      )}
      {tab === "ads" && <AdsTab purpose="services" />}

      {finding && <FindLeadsSheet side="demand" onClose={() => setFinding(false)} />}
    </>
  );
}

function FindMoreButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="find-more">
      <button className="btn btn--primary btn--sm" onClick={onClick}>
        Find more leads
      </button>
    </div>
  );
}
